import { useCart } from '../context/CartContext';
import { CartItem } from '../components/ui/CartItem';
import { Button } from '../components/ui/Button';
import { formatPrice } from '../utils/formatPrice';
import { X, ArrowLeft, Truck, Shield, Lock, ShoppingBag, Send } from 'lucide-react';
import { useState } from 'react';
import { generateOrderNumber, formatOrderMessage } from '../utils/generateOrderNumber';

export function CartSidebar() {
  const { items, isOpen, closeCart, itemCount, subtotal, clearCart, updateQuantity, removeFromCart } = useCart();
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    city: '',
    address: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCheckout = () => {
    if (items.length === 0) return;
    setShowCheckoutForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!customerInfo.name.trim() || !customerInfo.phone.trim() || !customerInfo.city.trim() || !customerInfo.address.trim()) {
      alert('Please fill in your Name, Phone, City, and Delivery Address.');
      return;
    }
    
    setIsSubmitting(true);
    const orderNumber = generateOrderNumber();
    const message = formatOrderMessage(items, customerInfo, orderNumber);
    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = '923056367673';
    const url = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    setTimeout(() => {
      window.open(url, '_blank');
      clearCart();
      closeCart();
      setShowCheckoutForm(false);
      setCustomerInfo({ name: '', phone: '', city: '', address: '', notes: '' });
      setIsSubmitting(false);
    }, 400);
  };

  const deliveryFee = subtotal >= 3000 ? 0 : 200;
  const total = subtotal + deliveryFee;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 transition-opacity"
        onClick={closeCart}
        aria-hidden="true"
      />
      
      {/* Drawer */}
      <aside className="fixed right-0 top-0 h-full w-full max-w-md md:max-w-lg bg-white z-50 shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 bg-white sticky top-0 z-10">
          {showCheckoutForm ? (
            <button
              onClick={() => setShowCheckoutForm(false)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-primary transition-colors py-1 px-2.5 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Cart ({itemCount})</span>
            </button>
          ) : (
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900 leading-tight">Your Cart</h2>
                <p className="text-xs text-gray-500">{itemCount} {itemCount === 1 ? 'item' : 'items'} added</p>
              </div>
            </div>
          )}

          <button
            onClick={closeCart}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            aria-label="Close cart"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        {showCheckoutForm ? (
          <div className="flex-1 overflow-y-auto p-5 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-primary/5 rounded-2xl p-4 border border-primary/15 mb-4">
                <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-0.5">Quick Checkout</p>
                <h3 className="text-base font-bold text-gray-900">Delivery Information</h3>
                <p className="text-xs text-gray-500 mt-1">Cash on Delivery across Pakistan</p>
              </div>
              
              <div>
                <label htmlFor="checkout-name" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="checkout-name"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                  className="input-field"
                  required
                  placeholder="e.g. Wajeeh Ur Rehman"
                />
              </div>

              <div>
                <label htmlFor="checkout-phone" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Phone Number (WhatsApp) <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="checkout-phone"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                  className="input-field"
                  required
                  placeholder="0300-1234567"
                />
              </div>

              <div>
                <label htmlFor="checkout-city" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="checkout-city"
                  value={customerInfo.city}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, city: e.target.value })}
                  className="input-field"
                  required
                  placeholder="e.g. Lahore, Karachi, Islamabad, Faisalabad"
                />
              </div>

              <div>
                <label htmlFor="checkout-address" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Complete Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="checkout-address"
                  value={customerInfo.address}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                  className="input-field min-h-[85px] resize-y"
                  required
                  placeholder="House/Street #, Mohalla, Area or Landmark..."
                />
              </div>

              <div>
                <label htmlFor="checkout-notes" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Order Notes <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  id="checkout-notes"
                  value={customerInfo.notes}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                  className="input-field"
                  placeholder="Special instructions (e.g. call before delivery)..."
                />
              </div>

              {/* Price summary */}
              <div className="bg-gray-50 rounded-2xl p-4 space-y-2 border border-gray-100 mt-4">
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Subtotal ({itemCount} items)</span>
                  <span className="font-semibold text-gray-900">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Delivery Charges</span>
                  <span className="font-semibold">
                    {deliveryFee === 0 ? (
                      <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded-full">FREE</span>
                    ) : (
                      formatPrice(deliveryFee)
                    )}
                  </span>
                </div>
                {deliveryFee === 0 && (
                  <p className="text-[11px] text-green-600 font-medium">✓ You qualified for Free Delivery (over Rs. 3,000)!</p>
                )}
                <div className="border-t border-gray-200 pt-2.5 flex justify-between text-base font-bold">
                  <span className="text-gray-900">Total Payable</span>
                  <span className="text-primary text-lg">{formatPrice(total)}</span>
                </div>
                <p className="text-[11px] text-gray-400 text-center">Cash on Delivery • Free returns if damaged</p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2.5 pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 px-6 rounded-2xl bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold text-base shadow-lg shadow-green-600/30 hover:shadow-xl hover:shadow-green-600/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                >
                  <Send className="w-4 h-4" />
                  <span>{isSubmitting ? 'Opening WhatsApp...' : 'Place Order on WhatsApp'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowCheckoutForm(false)}
                  className="w-full py-2.5 px-4 text-xs font-semibold text-gray-500 hover:text-gray-800 transition-colors text-center"
                >
                  ← Edit Cart Items
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            {/* Cart Items List */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-5">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center text-gray-400 mb-4">
                    <ShoppingBag className="w-10 h-10" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Your cart is empty</h3>
                  <p className="text-gray-500 text-xs mb-6 max-w-xs">Explore our natural herbal remedies and add them to your cart.</p>
                  <Button onClick={closeCart} variant="primary" size="md">
                    Start Shopping
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => (
                    <CartItem
                      key={item.id}
                      item={item}
                      onUpdateQuantity={updateQuantity}
                      onRemove={removeFromCart}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Cart Footer */}
            {items.length > 0 && (
              <div className="border-t border-gray-100 p-4 sm:p-5 bg-gray-50/50 space-y-4">
                {/* Trust mini badges */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-white rounded-xl border border-gray-100 shadow-2xs">
                    <Truck className="w-4 h-4 text-green-600 mx-auto mb-0.5" />
                    <p className="text-[10px] font-semibold text-gray-700">Cash on Delivery</p>
                  </div>
                  <div className="p-2 bg-white rounded-xl border border-gray-100 shadow-2xs">
                    <Shield className="w-4 h-4 text-blue-600 mx-auto mb-0.5" />
                    <p className="text-[10px] font-semibold text-gray-700">100% Herbal</p>
                  </div>
                  <div className="p-2 bg-white rounded-xl border border-gray-100 shadow-2xs">
                    <Lock className="w-4 h-4 text-purple-600 mx-auto mb-0.5" />
                    <p className="text-[10px] font-semibold text-gray-700">DRAP Approved</p>
                  </div>
                </div>

                {/* Pricing Breakdown */}
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span className="font-semibold text-gray-900">{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Delivery</span>
                    <span className="font-semibold">
                      {deliveryFee === 0 ? (
                        <span className="text-green-600 font-bold">FREE</span>
                      ) : (
                        formatPrice(deliveryFee)
                      )}
                    </span>
                  </div>
                  {deliveryFee > 0 && (
                    <p className="text-[11px] text-amber-600 font-medium">
                      Add {formatPrice(3000 - subtotal)} more to get Free Delivery!
                    </p>
                  )}
                  <div className="border-t border-gray-200 pt-2 flex justify-between text-base font-bold">
                    <span className="text-gray-900">Total</span>
                    <span className="text-primary text-lg">{formatPrice(total)}</span>
                  </div>
                </div>

                {/* Checkout Trigger */}
                <button
                  onClick={handleCheckout}
                  className="w-full py-4 px-6 rounded-2xl bg-gradient-to-r from-primary via-primary-dark to-herbal-800 text-white font-bold text-base shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
                >
                  <ShoppingBag className="w-5 h-5" />
                  <span>Proceed to Checkout</span>
                </button>
              </div>
            )}
          </>
        )}
      </aside>
    </>
  );
}